import Vue from 'vue';
import axios from "axios";
import utils from "@/stores/utils";
import { replaceOrderVariables } from '@/utils/order-variables';

const state = {
    sending_order_form_errors: {},
    sending_order_form: false,
    message: "",
    receive_copy: false,
    recipients: [],
    billingaccount: null,
    billingaccount_loading: false,
    order: null,
    orderType: 'customerorder_to_customer',
};

function make_message(order, billingaccount, orderType) {
    // Use configured message from billing account based on order type
    let messageTemplate;
    
    if (orderType === 'customerorder_to_customer') {
        messageTemplate = billingaccount?.notification_texts?.customerorder_form_to_customer_text;
    } else if (orderType === 'customerorder_to_provider') {
        messageTemplate = billingaccount?.notification_texts?.customerorder_form_to_provider_text;
    } else if (orderType === 'providerorder_to_provider') {
        messageTemplate = billingaccount?.notification_texts?.providerorder_form_to_provider_text;
    }

    return replaceOrderVariables(messageTemplate, order, billingaccount, Vue.prototype.$translate);
}

const mutations = {
    updateSendingOrderFormErrors(state, errors) {
        state.sending_order_form_errors = errors;
    },
    updateSendingOrderForm(state, sending) {
        state.sending_order_form = sending;
    },
    updateMessage(state, message) {
        state.message = message;
    },
    updateReceiveCopy(state, receive) {
        state.receive_copy = receive;
    },
    updateRecipients(state, recipients) {
        state.recipients = recipients;
    },
    updateBillingaccount(state, billingaccount) {
        state.billingaccount = billingaccount;
        state.message = make_message(state.order, billingaccount, state.orderType);
    },
    updateBillingaccountLoading(state, loading) {
        state.billingaccount_loading = loading;
    },
    updateOrder(state, order) {
        state.order = order;
    },
    updateOrderType(state, orderType) {
        state.orderType = orderType;
    },
};

const actions = {
    updateMessage({ commit }, message) {
        commit("updateMessage", message);
    },
    updateReceiveCopy({ commit }, receive) {
        commit("updateReceiveCopy", receive);
    },
    updateRecipients({ commit }, recipients) {
        commit("updateRecipients", recipients);
    },
    async fetchBillingaccount({ commit, state }) {
        commit("updateBillingaccountLoading", true);
        try {
            let response;   
            if (state.orderType === 'customerorder_to_customer' || state.orderType === 'customerorder_to_provider') {
                response = await axios.get(`/api/customerorders/${state.order.id}/buyerbillingaccount/`);
            } else if (state.orderType === 'providerorder_to_provider') {
                response = await axios.get(`/api/billingaccounts/${state.order.billingaccount}/`);
            }
            commit("updateBillingaccount", response.data);
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBillingaccountLoading", false);
        }
    },
    async sendOrderForm({ commit, dispatch, state }, { orderType }) {
        commit("updateSendingOrderForm", true);
        commit("updateSendingOrderFormErrors", {});

        let url;
        if (orderType === 'customerorder_to_customer') {
            url = `/api/customerorders/${state.order.id}/sendformtocustomer/`;
        } else if (orderType === 'customerorder_to_provider') {
            url = `/api/customerorders/${state.order.id}/sendformtoprovider/`;
        } else if (orderType === 'providerorder_to_provider') {
            url = `/api/providerorders/${state.order.id}/sendformtoprovider/`;
        } else {
            throw new Error(`Unknown orderType: ${orderType}`);
        }
        
        try {
            const payload = {
                message: state.message,
                recipients: state.recipients,
                receive_copy: state.receive_copy,
            };
            
            return await axios.post(url, payload);
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateSendingOrderFormErrors", error.details || {});
            throw error;
        } finally {
            commit("updateSendingOrderForm", false);
        }
    },
    async init({ commit, dispatch }, params) {
        const { order, orderType } = params;
        
        await commit("updateOrderType", orderType || 'customerorder_to_customer');
        await commit("updateOrder", order);
        await commit("updateSendingOrderFormErrors", {});
        
        // Get emails based on order type
        let emails;
        if (orderType === 'customerorder_to_customer') {
            emails = order.buyer_data?.billing_address?.emails;
        } else if (orderType === 'customerorder_to_provider' || orderType === 'providerorder_to_provider') {
            emails = order.seller_data?.address?.emails;
        }
        
        await commit("updateRecipients", emails || []);
        await commit("updateBillingaccount", params.billingaccount);
        dispatch("fetchBillingaccount");
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};

